/**
 * Inspector for the selected parcel, building and units.
 *
 * Every economic action runs through here: buying a deed, constructing, letting a
 * unit, and asking the NPC market to evaluate a listing.
 */

import type { FloorPlanResponse, PropertyDetail } from "@parcelbound/contracts";
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { api, ApiError } from "./api";
import { npcLandlord } from "./format";
import { useApp } from "./store";
import { Window } from "./Window";

const BUILDABLE = [
  "house",
  "apartment",
  "offices",
  "retail_block",
  "restaurant",
  "warehouse",
  "workshop",
  "factory",
  "farm_building",
  "logistics_depot",
] as const;

function money(wire: string): string {
  const n = Number(wire);
  if (!Number.isFinite(n)) return wire;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}


/**
 * The rooms of one floor.
 *
 * Floors are fetched one at a time: the plan is derived server-side from the
 * footprint, so asking for the floor you are looking at costs nothing, and a
 * forty-storey tower never has to send forty floors of geometry to show one.
 * The map draws whatever is loaded here as a cutaway.
 */
function FloorPlanSection({
  buildingId,
  floors,
}: {
  buildingId: string;
  floors: number;
}): JSX.Element {
  const plan = useApp((s) => s.floorPlan);
  const setFloorPlan = useApp((s) => s.setFloorPlan);
  const selectedRoomId = useApp((s) => s.selectedRoomId);
  const selectRoom = useApp((s) => s.selectRoom);
  const session = useApp((s) => s.session);
  // Shared with the world's floor bar, so ↑/↓ there and here stay in step.
  const floor = useApp((s) => s.planFloor);
  const setFloor = useApp((s) => s.setPlanFloor);

  // The docked property card (PropertyCard.tsx) owns plan fetching; this
  // section reads whatever it put in the store for the same building.
  const showing = plan && plan.buildingId === buildingId && plan.floor === floor ? plan : null;
  const rooms = showing ? showing.units.flatMap((u) => u.rooms.filter((r) => r.sellable)) : [];
  const cheapest = rooms.reduce<number | null>((min, r) => {
    const v = Number(r.appraisedValue);
    return min === null || v < min ? v : min;
  }, null);

  return (
    <>
      <h3>Floor plan</h3>
      <div className="card">
        <div className="floor-stepper">
          <button
            disabled={floor === 0}
            onClick={() => setFloor(Math.max(0, floor - 1))}
            title="Down one floor"
          >
            ↓
          </button>
          <span>
            Floor {floor + 1} of {floors}
          </span>
          <button
            disabled={floor >= floors - 1}
            onClick={() => setFloor(Math.min(floors - 1, floor + 1))}
            title="Up one floor"
          >
            ↑
          </button>
        </div>

        {!showing && <p className="empty">Loading floor…</p>}

        {showing && (
          <>
            <div className="kv">
              <span className="k">
                Rooms for sale ({rooms.length})
              </span>
              <span className="v">
                {cheapest === null ? "—" : `from ${money(cheapest.toFixed(2))}`}
              </span>
            </div>
            {showing.units.map((u) => (
              <div key={u.id} className="floor-unit">
                {showing.units.length > 1 && (
                  <div className="kv">
                    <span className="k" style={{ fontWeight: 700, color: "var(--ink)" }}>
                      {u.label}
                    </span>
                    <span className="pill-tag">{u.use}</span>
                  </div>
                )}
                {u.rooms.map((r) => (
                  <button
                    key={r.id}
                    className={`room-row${selectedRoomId === r.id ? " selected" : ""}`}
                    onClick={() => selectRoom(selectedRoomId === r.id ? null : r.id)}
                  >
                    <span className="room-name">{r.label}</span>
                    <span className="room-meta">
                      Fl {r.floor} · {Math.round(r.areaM2)} m²
                    </span>
                    <span className={r.sellable && !r.owner ? "room-price" : "room-price muted"}>
                      {r.owner
                        ? r.owner.id === session?.id
                          ? "yours"
                          : r.owner.displayName
                        : r.sellable
                          ? money(r.appraisedValue)
                          : "shared"}
                    </span>
                  </button>
                ))}
              </div>
            ))}
            <BuyRoomAction plan={showing} selectedRoomId={selectedRoomId} />
            <p className="notice info" style={{ margin: "9px 0 0" }}>
              Click a green $ on a room to select it, then buy. One payment and the room is
              yours — no rent, no per-minute cost, ever.
            </p>
          </>
        )}
      </div>
    </>
  );
}

/** The purchase itself, for whichever room is selected on the plan. */
function BuyRoomAction({
  plan,
  selectedRoomId,
}: {
  plan: FloorPlanResponse;
  selectedRoomId: string | null;
}): JSX.Element | null {
  const notify = useApp((s) => s.notify);
  const setFloorPlan = useApp((s) => s.setFloorPlan);
  const [busy, setBusy] = useState(false);

  const room = plan.units
    .flatMap((u) => u.rooms)
    .find((r) => r.id === selectedRoomId && r.sellable && !r.owner);
  if (!room) return null;

  return (
    <button
      className="primary"
      style={{ width: "100%", marginTop: 8 }}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        api
          .buyRoom(plan.buildingId, plan.floor, room.id, room.appraisedValue)
          .then(async (r) => {
            notify("ok", `${room.label} is yours for ${money(r.pricePaid)} — one payment, forever.`);
            // Refresh the plan so the fill turns gold and the $ disappears.
            setFloorPlan(await api.floorPlan(plan.buildingId, plan.floor));
            useApp.getState().setDashboard(await api.dashboard());
          })
          .catch((err: unknown) => notify("err", describe(err)))
          .finally(() => setBusy(false));
      }}
    >
      Buy {room.label} · {money(room.appraisedValue)}
    </button>
  );
}

/**
 * Direct offer on somebody else's property. The amount is escrowed on
 * submission and stays reserved until the owner answers or the offer is
 * withdrawn from the Offers window.
 */
function OfferForm({
  propertyId,
  kind,
  suggested,
  busy,
  act,
}: {
  propertyId: string;
  kind: "land" | "building";
  suggested: string;
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const [amount, setAmount] = useState(suggested);
  useEffect(() => setAmount(suggested), [propertyId, suggested]);

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="field">
        <label htmlFor={`offer-${kind}`}>Offer the owner</label>
        <input id={`offer-${kind}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <button
        style={{ width: "100%" }}
        disabled={busy}
        onClick={() =>
          void act(async () => {
            const normalized = Number(amount).toFixed(2);
            await api.makeOffer(propertyId, kind, normalized);
            return `Offer of ${money(normalized)} sent — the money waits in escrow. Track it under Offers.`;
          })
        }
      >
        Make direct offer
      </button>
    </div>
  );
}

export function Inspector(): JSX.Element {
  const selectedId = useApp((s) => s.selectedId);
  const session = useApp((s) => s.session);
  const notify = useApp((s) => s.notify);
  const focused = useApp((s) => s.focused) === "inspector";
  const closeWindow = useApp((s) => s.closeWindow);
  const focusWindow = useApp((s) => s.focusWindow);

  const [detail, setDetail] = useState<PropertyDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [archetype, setArchetype] = useState<string>("house");
  const [floors, setFloors] = useState(2);
  const [rent, setRent] = useState("");
  const [tenantPolicy, setTenantPolicy] = useState<"npc" | "player" | "both">("both");

  const load = async (id: string): Promise<void> => {
    try {
      const d = await api.property(id);
      setDetail(d);
      if (d.units[0]) setRent(d.units[0].fairRentPerDay);
    } catch (err) {
      notify("err", describe(err));
      setDetail(null);
    }
  };

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void load(selectedId);
  }, [selectedId]);

  const chrome = {
    focused,
    onClose: () => closeWindow("inspector"),
    onFocus: () => focusWindow("inspector"),
    initial: { x: Math.max(16, window.innerWidth - 420), y: 96 },
    width: 386,
    maxHeight: 600,
  };

  const refreshAll = async (): Promise<void> => {
    if (selectedId) await load(selectedId);
    try {
      useApp.getState().setDashboard(await api.dashboard());
    } catch {
      // A stale HUD is not worth surfacing an error for.
    }
  };

  const act = async (fn: () => Promise<string>): Promise<void> => {
    setBusy(true);
    try {
      notify("ok", await fn());
      await refreshAll();
    } catch (err) {
      notify("err", describe(err));
      // A version conflict means our copy is stale; reload so a retry works.
      if (err instanceof ApiError && err.code === "VERSION_CONFLICT" && selectedId) {
        await load(selectedId);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!selectedId) {
    return (
      <Window title="Inspector" {...chrome}>
        <p className="empty">
          Click a parcel or building on the map to inspect it. Unclaimed land can be bought from the
          world authority at its appraised price.
        </p>
      </Window>
    );
  }

  if (!detail) {
    return (
      <Window title="Inspector" {...chrome}>
        <p className="empty">Loading…</p>
      </Window>
    );
  }

  const p = detail.parcel;
  const b = detail.building;
  const iOwnLand = p.owner?.id === session?.id;
  const iOwnBuilding = b?.owner?.id === session?.id;

  return (
    <Window title={b ? b.label : "Land parcel"} {...chrome}>
      <div style={{ marginBottom: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(iOwnLand || iOwnBuilding) && <span className="pill-tag mine">Yours</span>}
        {p.listing && <span className="pill-tag sale">For sale</span>}
        <span className="pill-tag">{p.landUse}</span>
      </div>

      <div className="card">
        <div className="kv">
          <span className="k">Area</span>
          <span className="v">{Math.round(p.areaM2).toLocaleString()} m²</span>
        </div>
        <div className="kv">
          <span className="k">Desirability</span>
          <span className="v">{(p.desirability * 100).toFixed(0)}%</span>
        </div>
        <div className="kv">
          <span className="k">Busyness</span>
          <span className="v">{p.busyness.toFixed(2)} / 3</span>
        </div>
        <div className="kv">
          <span className="k">Resource potential</span>
          <span className="v">{(p.resourcePotential * 100).toFixed(0)}%</span>
        </div>
        <div className="kv">
          <span className="k">Land appraisal</span>
          <span className="v">{money(p.appraisedValue)}</span>
        </div>
        <div className="kv">
          <span className="k">Land owner</span>
          <span className="v">{p.owner ? p.owner.displayName : "unclaimed"}</span>
        </div>
      </div>

      {!p.owner && (
        <button
          className="primary"
          style={{ width: "100%" }}
          disabled={busy}
          onClick={() =>
            void act(async () => {
              const r = await api.purchase(p.id, "land", p.appraisedValue, detail.version);
              return `Bought the land for ${money(r.pricePaid)}.`;
            })
          }
        >
          Buy land for {money(p.appraisedValue)}
        </button>
      )}

      {p.listing && !iOwnLand && (
        <button
          className="primary"
          style={{ width: "100%" }}
          disabled={busy}
          onClick={() =>
            void act(async () => {
              const r = await api.purchase(p.id, "land", p.listing!.price, detail.version);
              return `Bought the listed parcel for ${money(r.pricePaid)}.`;
            })
          }
        >
          Buy listing for {money(p.listing.price)}
        </button>
      )}

      {p.owner && !iOwnLand && (
        <OfferForm
          propertyId={p.id}
          kind="land"
          suggested={p.appraisedValue}
          busy={busy}
          act={act}
        />
      )}

      {b && (
        <>
          <h3>Structure</h3>
          <div className="card">
            <p className="subline">
              {b.label} · {b.floors} floor{b.floors === 1 ? "" : "s"} ·{" "}
              {Math.round(b.footprintM2 * b.floors).toLocaleString()} m²
            </p>
            <div className="kv">
              <span className="k">Appraised value</span>
              <span className="v">{money(b.appraisedValue)}</span>
            </div>
            <div className="kv">
              <span className="k">Foot traffic</span>
              <span className="v">{p.busyness.toFixed(1)}×</span>
            </div>
            <div className="kv">
              <span className="k">Height</span>
              <span className="v">
                {b.heightM.toFixed(1)} m · {Math.round(b.footprintM2).toLocaleString()} m² plate
              </span>
            </div>
            <div className="kv">
              <span className="k">Condition</span>
              <span className="v">{(b.condition * 100).toFixed(0)}%</span>
            </div>
            <div className="kv">
              <span className="k">Owner</span>
              <span className="v">
                {b.owner ? b.owner.displayName : `${npcLandlord(b.id)} (NPC landlord)`}
              </span>
            </div>
            <div className="kv">
              <span className="k">Units</span>
              <span className="v">
                {b.unitCount} · {b.vacantUnitCount} vacant
              </span>
            </div>
          </div>
          {!b.owner && (
            <button
              className="primary"
              style={{ width: "100%" }}
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const r = await api.purchase(b.id, "building", b.appraisedValue, detail.version);
                  return `Bought the building for ${money(r.pricePaid)}.`;
                })
              }
            >
              Buy building for {money(b.appraisedValue)}
            </button>
          )}

          {b.owner && !iOwnBuilding && (
            <OfferForm
              propertyId={b.id}
              kind="building"
              suggested={b.appraisedValue}
              busy={busy}
              act={act}
            />
          )}

          {iOwnBuilding && b.condition < 0.9 && (
            <button
              style={{ width: "100%", marginTop: 8 }}
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const r = await api.renovate(b.id);
                  return `Renovated to 100% for ${money(r.cost)}. Better condition, better rent.`;
                })
              }
            >
              Renovate ({(b.condition * 100).toFixed(0)}% condition)
            </button>
          )}

          <FloorPlanSection buildingId={b.id} floors={b.floors} />
        </>
      )}

      {detail.groundLease && (
        <>
          <h3>Ground lease</h3>
          <div className="card">
            <div className="kv">
              <span className="k">Ground rent / day</span>
              <span className="v">{money(detail.groundLease.rentPerDay)}</span>
            </div>
            <div className="kv">
              <span className="k">Regulated cap</span>
              <span className="v">{money(detail.groundLease.capPerDay)}</span>
            </div>
            <p className="notice info" style={{ margin: "9px 0 0" }}>
              The land and the building have different owners, so a perpetual ground lease applies.
              The rate is capped to prevent extortion.
            </p>
          </div>
        </>
      )}

      {iOwnLand && !b && (
        <>
          <h3>Construct</h3>
          <div className="card">
            <div className="field">
              <label htmlFor="arch">Archetype</label>
              <select id="arch" value={archetype} onChange={(e) => setArchetype(e.target.value)}>
                {BUILDABLE.map((a) => (
                  <option key={a} value={a}>
                    {a.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="floors">Floors</label>
              <input
                id="floors"
                type="number"
                min={1}
                max={40}
                value={floors}
                onChange={(e) => setFloors(Math.max(1, Math.min(40, Number(e.target.value) || 1)))}
              />
            </div>
            <button
              className="primary"
              style={{ width: "100%" }}
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const r = await api.construct(p.id, archetype, floors, detail.version);
                  return `Built for ${money(r.cost)}, creating ${r.unitsCreated} unit(s).`;
                })
              }
            >
              Build here
            </button>
            <p className="notice info" style={{ margin: "9px 0 0" }}>
              Only archetypes compatible with {p.landUse} land can be built here.
            </p>
          </div>
        </>
      )}

      {b && detail.units.length > 0 && (
        <>
          <h3>Units ({detail.units.length})</h3>
          {iOwnBuilding && (
            <div className="card">
              <div className="field">
                <label htmlFor="rent">Asking rent per game day</label>
                <input id="rent" value={rent} onChange={(e) => setRent(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="policy">Tenants allowed</label>
                <select
                  id="policy"
                  value={tenantPolicy}
                  onChange={(e) => setTenantPolicy(e.target.value as typeof tenantPolicy)}
                >
                  <option value="both">NPCs and players</option>
                  <option value="npc">NPCs only</option>
                  <option value="player">Players only</option>
                </select>
              </div>
              <p className="notice info" style={{ margin: 0 }}>
                Asking near the fair rent shown on each unit fills it quickly. Overpricing empties it.
              </p>
            </div>
          )}
          {detail.units.map((u) => (
            <div className="card" key={u.id}>
              <div className="kv">
                <span className="k" style={{ fontWeight: 700, color: "var(--ink)" }}>
                  {u.label}
                </span>
                <span className="pill-tag">{u.use}</span>
              </div>
              <div className="kv">
                <span className="k">Area</span>
                <span className="v">
                  Floor {u.floor} · {u.areaM2.toFixed(1)} m²
                </span>
              </div>
              <div className="kv">
                <span className="k">Appraised value</span>
                <span className="v">{money(u.appraisedValue)}</span>
              </div>
              <div className="kv">
                <span className="k">Fair rent / day</span>
                <span className="v">{money(u.fairRentPerDay)}</span>
              </div>
              {u.occupant ? (
                <div className="kv">
                  <span className="k">Tenant · {u.occupant.kind}</span>
                  <span className="v up">
                    {u.occupant.name} · {money(u.occupant.rentPerDay)}
                  </span>
                </div>
              ) : u.listing ? (
                <>
                  <div className="kv">
                    <span className="k">Listed at</span>
                    <span className="v">{money(u.listing.rentPerDay)}</span>
                  </div>
                  {iOwnBuilding && (
                    <div className="row-actions">
                      <button
                        disabled={busy}
                        onClick={() =>
                          void act(async () => {
                            const r = await api.seekNpcTenant(u.listing!.id);
                            return r.accepted
                              ? `${r.tenantName} moved in (demand ${r.demand}).`
                              : `No applicant — demand ${r.demand}. ${r.hint ?? ""}`;
                          })
                        }
                      >
                        Seek NPC tenant
                      </button>
                    </div>
                  )}
                </>
              ) : (
                iOwnBuilding && (
                  <div className="row-actions">
                    <button
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await api.createLeaseListing({
                            unitId: u.id,
                            rentPerDay: rent || u.fairRentPerDay,
                            deposit: "0.00",
                            leaseDays: 30,
                            tenantPolicy,
                            autoRenew: true,
                          });
                          return `${u.label} listed for rent.`;
                        })
                      }
                    >
                      List for rent
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </>
      )}
    </Window>
  );
}
